import { Hono } from "hono";

import type { GencowCtx, GencowProcedureDef, QueryDef } from "@gencow/core";
import type { FunctionType } from "./server-execution.js";
import { ProcedureRpcDecodeError, ProcedureRpcHandler } from "@gencow/client/procedure-rpc";
import { buildRpcErrorBody } from "./server-rpc-error.js";

type LoggerLike = Pick<Console, "warn" | "error">;

type ExecuteWithTimeout = <T>(fn: () => Promise<T>, kind: FunctionType, name: string) => Promise<T>;

type MutationLike = {
  name: string;
  isPublic: boolean;
  argsSchema?: unknown;
  handler(ctx: GencowCtx, args: unknown): Promise<unknown>;
};

type MutationMap = Map<string, MutationLike>;

type ProcedureQueryMap = Map<string, GencowProcedureDef<"query", any, any, any>>;
type ProcedureMutationMap = Map<string, GencowProcedureDef<"mutation", any, any, any>>;

export type RpcWireMode = "legacy" | "procedure";

export type RpcLogContext = {
  rpcMode: RpcWireMode;
  routePath: string;
};

type QueryContext = {
  req: {
    json(): Promise<unknown>;
    path?: string;
  };
  json(body: unknown, status?: number): unknown;
  rpcLogContext?: RpcLogContext;
};

type MutationContext = {
  req: {
    header(name: string): string | undefined;
    parseBody(): Promise<Record<string, unknown>>;
    formData(): Promise<FormData>;
    json(): Promise<unknown>;
    path?: string;
  };
  json(body: unknown, status?: number): unknown;
  rpcLogContext?: RpcLogContext;
};

function formatRpcLogPrefix(
  rpcLogContext: RpcLogContext | undefined,
  fallback: "query" | "mutation" | "procedure.query" | "procedure.mutation",
): string {
  if (rpcLogContext) {
    return `[${rpcLogContext.routePath} rpcMode=${rpcLogContext.rpcMode}]`;
  }
  return `[${fallback}]`;
}

function resolveRpcRoutePath(c: { req: { path?: string } }, fallback: string): string {
  return c.req.path ?? fallback;
}

function withRpcLogContext<T extends QueryContext | MutationContext>(
  c: T,
  rpcMode: RpcWireMode,
  routePath: string,
): T & { rpcLogContext: RpcLogContext } {
  return { ...c, rpcLogContext: { rpcMode, routePath } };
}

function procedureRpcDecodeErrorMessage(error: unknown): string | undefined {
  if (error instanceof ProcedureRpcDecodeError) {
    return error.message;
  }
  if (
    error &&
    typeof error === "object" &&
    (error as { code?: string }).code === "RPC_DECODE_ERROR" &&
    typeof (error as { message?: string }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return undefined;
}

type ValidationErrorCtor = new (...args: any[]) => Error;
const INTERNAL_MUTATION_PREFIX = "__gencow.";
const QUERY_TRANSIENT_RETRY_ATTEMPTS = 2;
const TRANSIENT_QUERY_ERROR_CODES = new Set([
  "ERR_POSTGRES_IDLE_TIMEOUT",
  "08000",
  "08001",
  "08003",
  "08004",
  "08006",
  "08P01",
  "40001",
  "40P01",
  "53300",
  "55P03",
  "57P01",
  "57P02",
  "57P03",
]);
const TRANSIENT_QUERY_MESSAGE_PATTERNS = [
  "idle timeout reached",
  "connection terminated",
  "server closed the connection",
  "connection reset",
  "econnreset",
  "etimedout",
];

function collectErrorSignals(error: unknown, signals: string[] = [], depth = 0): string[] {
  if (!error || depth > 4) {
    return signals;
  }

  if (typeof error === "string") {
    signals.push(error);
    return signals;
  }

  if (typeof error !== "object") {
    return signals;
  }

  const record = error as Record<string, unknown>;
  if (typeof record.code === "string") {
    signals.push(record.code);
  }
  if (typeof record.message === "string") {
    signals.push(record.message);
  }
  if (record.cause) {
    collectErrorSignals(record.cause, signals, depth + 1);
  }
  return signals;
}

function isTransientQueryFailure(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    if (typeof record.status === "number" || record.code === "FUNCTION_TIMEOUT") {
      return false;
    }
  }

  for (const signal of collectErrorSignals(error)) {
    if (TRANSIENT_QUERY_ERROR_CODES.has(signal)) {
      return true;
    }
    const lowerSignal = signal.toLowerCase();
    if (TRANSIENT_QUERY_MESSAGE_PATTERNS.some((pattern) => lowerSignal.includes(pattern))) {
      return true;
    }
  }
  return false;
}

function describeTransientQueryFailure(error: unknown): string {
  const signals = collectErrorSignals(error);
  const code = signals.find((signal) => TRANSIENT_QUERY_ERROR_CODES.has(signal));
  if (code) {
    return code;
  }
  return signals[0] ?? "unknown transient failure";
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function executeQueryWithTransientRetry<T>(params: {
  executeWithTimeout: ExecuteWithTimeout;
  name: string;
  logPrefix: string;
  logger: LoggerLike;
  fn: () => Promise<T>;
}): Promise<T> {
  let attempt = 0;
  let lastError: unknown;
  while (attempt < QUERY_TRANSIENT_RETRY_ATTEMPTS) {
    attempt += 1;
    try {
      return await params.executeWithTimeout(params.fn, "query", params.name);
    } catch (error) {
      lastError = error;
      if (attempt >= QUERY_TRANSIENT_RETRY_ATTEMPTS || !isTransientQueryFailure(error)) {
        throw error;
      }

      params.logger.warn(
        `${params.logPrefix} ${params.name} transient failure; retrying once: ${describeTransientQueryFailure(error)}`,
      );
      await sleep(25);
    }
  }

  throw lastError;
}

export function createMixedUserIdChecker(params: {
  warnedQueries?: Set<string>;
  logger?: LoggerLike;
}): (queryName: string, result: any[]) => void {
  const warnedQueries = params.warnedQueries ?? new Set<string>();
  const logger = params.logger ?? console;

  return (queryName: string, result: any[]) => {
    if (warnedQueries.has(queryName)) {
      return;
    }

    const userIdKeys = ["userId", "user_id", "ownerId", "owner_id", "authorId", "author_id"];
    for (const key of userIdKeys) {
      const values = new Set<string>();
      for (const row of result) {
        if (row && typeof row === "object" && key in row && row[key] != null) {
          values.add(String(row[key]));
        }
      }
      if (values.size >= 2) {
        warnedQueries.add(queryName);
        logger.warn(`[gencow] ⚠️  "${queryName}" returned rows with ${values.size} different ${key} values.`);
        logger.warn(
          `[gencow]    💡 This may indicate missing data isolation. Use pgTable + ownerRls() + createCrud() to auto-isolate user data.`,
        );
        return;
      }
    }
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function objectHasOwn(o: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(o, key);
}

/** Procedure wire: top-level `json` without top-level `name` (see unified RPC spec). */
export function isProcedureJsonWire(body: unknown): boolean {
  return isRecord(body) && objectHasOwn(body, "json") && !objectHasOwn(body, "name");
}

function isAmbiguousJsonWire(body: unknown): boolean {
  return isRecord(body) && objectHasOwn(body, "json") && objectHasOwn(body, "name");
}

const AMBIGUOUS_JSON_RPC_ERROR =
  'Ambiguous request body: cannot include both top-level "name" and top-level "json".';

const UNKNOWN_RPC_NAME_ERROR = "Unknown RPC operation: name is not registered in legacy or procedure map.";

type MutationMultipartMode = "procedure" | "legacy" | "ambiguous" | "missing";

function detectMutationMultipartMode(form: FormData): MutationMultipartMode {
  const hasData = form.has("data");
  const hasMutation = form.has("_mutation");
  if (hasData && hasMutation) {
    return "ambiguous";
  }
  if (hasData) {
    return "procedure";
  }
  if (hasMutation) {
    return "legacy";
  }
  return "missing";
}

export type RpcJsonResolveMode = RpcWireMode | "ambiguous" | "unknown";

export function resolveQueryJsonRpcMode(params: {
  body: unknown;
  getQueryDef(name: string): QueryDef | undefined;
  queryProcedureMap: ProcedureQueryMap;
}): RpcJsonResolveMode {
  if (isAmbiguousJsonWire(params.body)) {
    return "ambiguous";
  }
  if (isProcedureJsonWire(params.body)) {
    return "procedure";
  }
  if (isRecord(params.body) && typeof params.body.name === "string") {
    const name = params.body.name;
    if (params.getQueryDef(name)) {
      return "legacy";
    }
    if (params.queryProcedureMap.has(name)) {
      return "procedure";
    }
    return "unknown";
  }
  return "legacy";
}

export function resolveMutationJsonRpcMode(params: {
  body: unknown;
  mutationMap: MutationMap;
  mutationProcedureMap: ProcedureMutationMap;
}): RpcJsonResolveMode {
  if (isAmbiguousJsonWire(params.body)) {
    return "ambiguous";
  }
  if (isProcedureJsonWire(params.body)) {
    return "procedure";
  }
  if (isRecord(params.body) && typeof params.body.name === "string") {
    const name = params.body.name;
    if (params.mutationMap.has(name)) {
      return "legacy";
    }
    if (params.mutationProcedureMap.has(name)) {
      return "procedure";
    }
    return "unknown";
  }
  return "legacy";
}

export async function parseMutationRequest(params: {
  contentType?: string;
  parseBody(): Promise<Record<string, unknown>>;
  json(): Promise<unknown>;
}): Promise<{ name: unknown; args: unknown }> {
  if ((params.contentType ?? "").includes("multipart/form-data")) {
    const body = await params.parseBody();
    const name = body._mutation;
    delete body._mutation;
    return { name, args: body };
  }

  const body = (await params.json().catch(() => ({}))) as { name?: unknown; args?: unknown };
  return { name: body.name, args: body.args };
}

export function createApiRootPayload(uptimeSeconds: () => number = () => process.uptime()) {
  return {
    status: "running",
    version: "0.1.0",
    uptime: Math.floor(uptimeSeconds()),
    endpoints: {
      query: "POST /api/query",
      mutation: "POST /api/mutation",
      storage: "GET /api/storage/:id",
      auth: "/api/auth/*",
    },
  };
}

export function createQueryRouteHandler(params: {
  getQueryDef(name: string): QueryDef | undefined;
  buildCtx(context: QueryContext): any;
  executeWithTimeout: ExecuteWithTimeout;
  parseArgs(schema: unknown, args: unknown): unknown;
  validationErrorCtor: ValidationErrorCtor;
  isBaas: boolean;
  publicQueryWarned?: Set<string>;
  checkMixedUserIds?: (queryName: string, result: any[]) => void;
  logger?: LoggerLike;
}) {
  const publicQueryWarned = params.publicQueryWarned ?? new Set<string>();
  const checkMixedUserIds = params.checkMixedUserIds ?? (() => {});
  const logger = params.logger ?? console;

  return async (c: QueryContext) => {
    const logPrefix = formatRpcLogPrefix(c.rpcLogContext, "query");
    const body = (await c.req.json().catch(() => ({}))) as { name?: string; args?: unknown };
    const name = body.name;
    if (!name) {
      return c.json({ error: "Missing query name" }, 400);
    }

    const queryDef = params.getQueryDef(name);
    if (!queryDef) {
      return c.json({ error: `Query not found: ${name}` }, 404);
    }

    const ctx = params.buildCtx(c);
    if (!queryDef.isPublic) {
      const user = ctx.auth.getUserIdentity();
      if (!user) {
        return c.json({ error: "Authentication required" }, 401);
      }
    }

    try {
      const validatedArgs = params.parseArgs(queryDef.argsSchema, body.args);
      const result = await executeQueryWithTransientRetry({
        executeWithTimeout: params.executeWithTimeout,
        name,
        logPrefix,
        logger,
        fn: () => queryDef.handler(ctx, validatedArgs),
      });

      if (queryDef.isPublic && !params.isBaas && Array.isArray(result) && result.length >= 100) {
        if (!publicQueryWarned.has(name)) {
          publicQueryWarned.add(name);
          logger.warn(`[gencow] ⚠️  "${name}" (isPublic) returned ${result.length} rows without pagination.`);
          logger.warn("[gencow]    💡 Add .limit(100) to prevent full table scans. See SECURITY.md");
        }
      }

      if (!params.isBaas && Array.isArray(result) && result.length >= 2) {
        checkMixedUserIds(name, result);
      }

      return c.json(result ?? null);
    } catch (error: any) {
      const status =
        typeof error?.status === "number"
          ? error.status
          : error?.code === "FUNCTION_TIMEOUT"
            ? 408
            : error instanceof params.validationErrorCtor
              ? 400
              : 500;
      if (status === 500) {
        logger.error(`${logPrefix} ${name} failed:`, error.message);
        if (error.cause) {
          logger.error(`${logPrefix}   cause:`, error.cause);
        }
      }
      return c.json(buildRpcErrorBody(error), status);
    }
  };
}

export function createMutationRouteHandler(params: {
  mutationMap: MutationMap;
  buildCtx(context: MutationContext): any;
  executeWithTimeout: ExecuteWithTimeout;
  parseArgs(schema: unknown, args: unknown): unknown;
  validationErrorCtor: ValidationErrorCtor;
  logger?: LoggerLike;
}) {
  const logger = params.logger ?? console;

  return async (c: MutationContext) => {
    const logPrefix = formatRpcLogPrefix(c.rpcLogContext, "mutation");
    const { name, args } = await parseMutationRequest({
      contentType: c.req.header("content-type") || "",
      parseBody: () => c.req.parseBody(),
      json: () => c.req.json(),
    });

    if (!name) {
      return c.json({ error: "Missing mutation name" }, 400);
    }
    if (String(name).startsWith(INTERNAL_MUTATION_PREFIX)) {
      return c.json({ error: `Mutation not found: ${name}` }, 404);
    }

    const mutation = params.mutationMap.get(name as string);
    if (!mutation) {
      return c.json({ error: `Mutation not found: ${name}` }, 404);
    }

    const ctx = params.buildCtx(c);
    if (!mutation.isPublic) {
      const user = ctx.auth.getUserIdentity();
      if (!user) {
        return c.json({ error: "Authentication required" }, 401);
      }
    }

    try {
      const validatedArgs = params.parseArgs(mutation.argsSchema, args);
      const result = await params.executeWithTimeout(
        () => mutation.handler(ctx, validatedArgs),
        "mutation",
        name as string,
      );

      const realtime = ctx.realtime as any;
      if (realtime._flushRefresh) {
        await realtime._flushRefresh();
      }
      if (realtime._flushRealtimeEvents) {
        await realtime._flushRealtimeEvents();
      }
      if (realtime._hasEmitted !== undefined && !realtime._hasEmitted) {
        logger.warn(
          `[gencow] ⚠️  mutation "${name}" completed without ctx.realtime.emit(), ctx.realtime.invalidate(), or ctx.realtime.refresh(). Subscribed clients won't see updates. 💡 Use ctx.realtime.invalidate("queryKey") for private/RLS queries.`,
        );
      }

      return c.json(result ?? null, 201);
    } catch (error: any) {
      logger.error(`${logPrefix} "${name}" failed:`, error.message);
      if (error.cause) {
        logger.error(`${logPrefix}   cause:`, error.cause);
      }
      const status =
        typeof error?.status === "number"
          ? error.status
          : error?.code === "FUNCTION_TIMEOUT"
            ? 408
            : error instanceof params.validationErrorCtor
              ? 400
              : 500;
      return c.json(buildRpcErrorBody(error), status);
    }
  };
}

/**
 * procedure.query procedures from `defineApi({ procedures })` — Standard Schema validation runs inside the handler chain;
 * request body args are passed through without legacy parseArgs().
 */
export function createProcedureQueryRouteHandler(params: {
  queryProcedureMap: ProcedureQueryMap;
  buildCtx(context: QueryContext): any;
  executeWithTimeout: ExecuteWithTimeout;
  validationErrorCtor: ValidationErrorCtor;
  isBaas: boolean;
  procedureRpcHandler?: ProcedureRpcHandler;
  publicQueryWarned?: Set<string>;
  checkMixedUserIds?: (queryName: string, result: any[]) => void;
  logger?: LoggerLike;
}) {
  const publicQueryWarned = params.publicQueryWarned ?? new Set<string>();
  const checkMixedUserIds = params.checkMixedUserIds ?? (() => {});
  const logger = params.logger ?? console;
  const procedureRpcHandler = params.procedureRpcHandler ?? new ProcedureRpcHandler();

  return async (c: QueryContext) => {
    const logPrefix = formatRpcLogPrefix(c.rpcLogContext, "procedure.query");
    let step: "decode_input" | "call_procedure" | undefined;
    let queryNameForLogs = "unknown";
    try {
      step = "decode_input";
      const body = procedureRpcHandler.decodeProcedureRpcBody(await c.req.json());
      const name = body.name as string | undefined;
      queryNameForLogs = String(name ?? "unknown");
      if (!name) {
        return c.json({ error: "Missing query name" }, 400);
      }

      const proc = params.queryProcedureMap.get(name);
      if (!proc) {
        return c.json({ error: `Query not found: ${name}` }, 404);
      }

      const ctx = params.buildCtx(c);
      if (!proc.allowAnonymous) {
        const user = ctx.auth.getUserIdentity();
        if (!user) {
          return c.json({ error: "Authentication required" }, 401);
        }
      }

      step = "call_procedure";
      const result = await executeQueryWithTransientRetry({
        executeWithTimeout: params.executeWithTimeout,
        name,
        logPrefix,
        logger,
        fn: () => proc.handler(ctx, body.args as never),
      });
      step = undefined;

      if (proc.allowAnonymous && !params.isBaas && Array.isArray(result) && result.length >= 100) {
        if (!publicQueryWarned.has(name)) {
          publicQueryWarned.add(name);
          logger.warn(
            `[gencow] ⚠️  "${name}" (allowAnonymous) returned ${result.length} rows without pagination.`,
          );
          logger.warn("[gencow]    💡 Add .limit(100) to prevent full table scans. See SECURITY.md");
        }
      }

      if (!params.isBaas && Array.isArray(result) && result.length >= 2) {
        checkMixedUserIds(name, result);
      }

      return c.json(procedureRpcHandler.encodeWirePayload(result ?? null));
    } catch (error: any) {
      const status =
        step === "decode_input"
          ? 400
          : typeof error?.status === "number"
            ? error.status
            : error?.code === "FUNCTION_TIMEOUT"
              ? 408
              : error instanceof params.validationErrorCtor
                ? 400
                : 500;
      if (status === 500) {
        logger.error(`${logPrefix} ${queryNameForLogs} failed:`, error.message);
        if (error.cause) {
          logger.error(`${logPrefix}   cause:`, error.cause);
        }
      }
      const message =
        step === "decode_input"
          ? "Malformed request. Ensure the request body is properly formatted and the 'Content-Type' header is set correctly."
          : error.message;
      return c.json(buildRpcErrorBody(error, message), status);
    }
  };
}

export function createProcedureMutationRouteHandler(params: {
  mutationProcedureMap: ProcedureMutationMap;
  buildCtx(context: MutationContext): any;
  executeWithTimeout: ExecuteWithTimeout;
  validationErrorCtor: ValidationErrorCtor;
  procedureRpcHandler?: ProcedureRpcHandler;
  logger?: LoggerLike;
}) {
  const logger = params.logger ?? console;
  const procedureRpcHandler = params.procedureRpcHandler ?? new ProcedureRpcHandler();

  return async (c: MutationContext) => {
    const logPrefix = formatRpcLogPrefix(c.rpcLogContext, "procedure.mutation");
    let step: "decode_input" | "call_procedure" | undefined;
    let mutationNameForLogs = "unknown";
    let rpcFormat: "json" | "multipart" = "json";
    let blobCount = 0;
    try {
      step = "decode_input";
      const contentType = c.req.header("content-type") ?? "";
      let decoded: { name: unknown; args: unknown };
      if (contentType.includes("multipart/form-data")) {
        rpcFormat = "multipart";
        const form = await c.req.formData();
        blobCount = [...form.keys()].filter((key) => /^\d+$/.test(key)).length;
        decoded = procedureRpcHandler.decodeProcedureMultipart(form);
      } else {
        decoded = procedureRpcHandler.decodeProcedureRpcBody(await c.req.json());
      }
      const { name, args } = decoded;

      mutationNameForLogs = String(name ?? "unknown");
      if (!name) {
        return c.json({ error: "Missing mutation name" }, 400);
      }
      if (String(name).startsWith(INTERNAL_MUTATION_PREFIX)) {
        return c.json({ error: `Mutation not found: ${name}` }, 404);
      }

      const proc = params.mutationProcedureMap.get(name as string);
      if (!proc) {
        return c.json({ error: `Mutation not found: ${name}` }, 404);
      }

      const ctx = params.buildCtx(c);
      if (!proc.allowAnonymous) {
        const user = ctx.auth.getUserIdentity();
        if (!user) {
          return c.json({ error: "Authentication required" }, 401);
        }
      }

      step = "call_procedure";
      const result = await params.executeWithTimeout(
        () => proc.handler(ctx, args as never),
        "mutation",
        name as string,
      );
      step = undefined;

      const realtime = ctx.realtime as any;
      if (realtime._flushRefresh) {
        await realtime._flushRefresh();
      }
      if (realtime._flushRealtimeEvents) {
        await realtime._flushRealtimeEvents();
      }
      if (realtime._hasEmitted !== undefined && !realtime._hasEmitted) {
        logger.warn(
          `[gencow] ⚠️  mutation "${name}" completed without ctx.realtime.emit(), ctx.realtime.invalidate(), or ctx.realtime.refresh(). Subscribed clients won't see updates. 💡 Use ctx.realtime.invalidate("queryKey") for private/RLS queries.`,
        );
      }

      return c.json(procedureRpcHandler.encodeWirePayload(result ?? null), 201);
    } catch (error: any) {
      const decodeDetail = rpcFormat === "multipart" ? ` rpcFormat=multipart blobCount=${blobCount}` : "";
      logger.error(`${logPrefix} "${mutationNameForLogs}" failed${decodeDetail}:`, error.message);
      if (error.cause) {
        logger.error(`${logPrefix}   cause:`, error.cause);
      }
      const status =
        step === "decode_input"
          ? 400
          : typeof error?.status === "number"
            ? error.status
            : error?.code === "FUNCTION_TIMEOUT"
              ? 408
              : error instanceof params.validationErrorCtor
                ? 400
                : 500;
      const decodeMessage = procedureRpcDecodeErrorMessage(error);
      const message =
        step === "decode_input"
          ? (decodeMessage ??
            "Malformed request. Ensure the request body is properly formatted and the 'Content-Type' header is set correctly.")
          : error.message;
      return c.json(buildRpcErrorBody(error, message), status);
    }
  };
}

export function createUnifiedQueryRouteHandler(params: {
  getQueryDef(name: string): QueryDef | undefined;
  queryProcedureMap: ProcedureQueryMap;
  buildCtx(context: QueryContext): any;
  executeWithTimeout: ExecuteWithTimeout;
  parseArgs(schema: unknown, args: unknown): unknown;
  validationErrorCtor: ValidationErrorCtor;
  isBaas: boolean;
  procedureRpcHandler?: ProcedureRpcHandler;
  publicQueryWarned?: Set<string>;
  checkMixedUserIds?: (queryName: string, result: any[]) => void;
  logger?: LoggerLike;
}) {
  const legacyHandler = createQueryRouteHandler({
    getQueryDef: params.getQueryDef,
    buildCtx: params.buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    parseArgs: params.parseArgs,
    validationErrorCtor: params.validationErrorCtor,
    isBaas: params.isBaas,
    publicQueryWarned: params.publicQueryWarned,
    checkMixedUserIds: params.checkMixedUserIds,
    logger: params.logger,
  });
  const procedureHandler = createProcedureQueryRouteHandler({
    queryProcedureMap: params.queryProcedureMap,
    buildCtx: params.buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    validationErrorCtor: params.validationErrorCtor,
    isBaas: params.isBaas,
    procedureRpcHandler: params.procedureRpcHandler,
    publicQueryWarned: params.publicQueryWarned,
    checkMixedUserIds: params.checkMixedUserIds,
    logger: params.logger,
  });

  return async (c: QueryContext) => {
    const routePath = resolveRpcRoutePath(c, "/api/query");
    const body = await c.req.json().catch(() => ({}));
    const mode = resolveQueryJsonRpcMode({
      body,
      getQueryDef: params.getQueryDef,
      queryProcedureMap: params.queryProcedureMap,
    });
    if (mode === "ambiguous") {
      return c.json({ error: AMBIGUOUS_JSON_RPC_ERROR, code: "RPC_DECODE_ERROR" }, 400);
    }
    if (mode === "unknown") {
      return c.json({ error: UNKNOWN_RPC_NAME_ERROR, code: "RPC_DECODE_ERROR" }, 400);
    }
    const jsonReq = { json: async () => body };
    if (mode === "legacy") {
      return legacyHandler(withRpcLogContext({ ...c, req: { ...c.req, ...jsonReq } }, "legacy", routePath));
    }
    return procedureHandler(
      withRpcLogContext({ ...c, req: { ...c.req, ...jsonReq } }, "procedure", routePath),
    );
  };
}

export function createUnifiedMutationRouteHandler(params: {
  mutationMap: MutationMap;
  mutationProcedureMap: ProcedureMutationMap;
  buildCtx(context: MutationContext): any;
  executeWithTimeout: ExecuteWithTimeout;
  parseArgs(schema: unknown, args: unknown): unknown;
  validationErrorCtor: ValidationErrorCtor;
  procedureRpcHandler?: ProcedureRpcHandler;
  logger?: LoggerLike;
}) {
  const legacyHandler = createMutationRouteHandler({
    mutationMap: params.mutationMap,
    buildCtx: params.buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    parseArgs: params.parseArgs,
    validationErrorCtor: params.validationErrorCtor,
    logger: params.logger,
  });
  const procedureHandler = createProcedureMutationRouteHandler({
    mutationProcedureMap: params.mutationProcedureMap,
    buildCtx: params.buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    validationErrorCtor: params.validationErrorCtor,
    procedureRpcHandler: params.procedureRpcHandler,
    logger: params.logger,
  });

  return async (c: MutationContext) => {
    const routePath = resolveRpcRoutePath(c, "/api/mutation");
    const contentType = c.req.header("content-type") ?? "";
    if (contentType.includes("multipart/form-data")) {
      const form = await c.req.formData();
      const multipartMode = detectMutationMultipartMode(form);
      if (multipartMode === "ambiguous") {
        return c.json(
          {
            error: 'Ambiguous multipart request: cannot include both "data" and "_mutation" fields.',
            code: "RPC_DECODE_ERROR",
          },
          400,
        );
      }
      if (multipartMode === "missing") {
        return c.json(
          {
            error: 'Multipart mutation must include either "data" (procedure) or "_mutation" (legacy).',
            code: "RPC_DECODE_ERROR",
          },
          400,
        );
      }
      if (multipartMode === "procedure") {
        return procedureHandler(
          withRpcLogContext(
            {
              ...c,
              req: {
                ...c.req,
                header: () => contentType,
                formData: async () => form,
                parseBody: async () => {
                  throw new Error("parseBody must not be used for procedure multipart");
                },
                json: async () => {
                  throw new Error("json must not be used for procedure multipart");
                },
              },
            },
            "procedure",
            routePath,
          ),
        );
      }
      return legacyHandler(
        withRpcLogContext(
          {
            ...c,
            req: {
              ...c.req,
              header: () => contentType,
              formData: async () => form,
              parseBody: async () => {
                const record: Record<string, unknown> = {};
                for (const [key, value] of form.entries()) {
                  record[key] = value;
                }
                return record;
              },
              json: async () => {
                throw new Error("json must not be used for legacy multipart");
              },
            },
          },
          "legacy",
          routePath,
        ),
      );
    }

    const body = await c.req.json().catch(() => ({}));
    const mode = resolveMutationJsonRpcMode({
      body,
      mutationMap: params.mutationMap,
      mutationProcedureMap: params.mutationProcedureMap,
    });
    if (mode === "ambiguous") {
      return c.json({ error: AMBIGUOUS_JSON_RPC_ERROR, code: "RPC_DECODE_ERROR" }, 400);
    }
    if (mode === "unknown") {
      return c.json({ error: UNKNOWN_RPC_NAME_ERROR, code: "RPC_DECODE_ERROR" }, 400);
    }
    const jsonReq = { json: async () => body };
    if (mode === "legacy") {
      return legacyHandler(
        withRpcLogContext(
          {
            ...c,
            req: {
              ...c.req,
              header: () => contentType,
              ...jsonReq,
              parseBody: () => c.req.parseBody(),
            },
          },
          "legacy",
          routePath,
        ),
      );
    }
    return procedureHandler(
      withRpcLogContext(
        {
          ...c,
          req: {
            ...c.req,
            header: () => contentType,
            ...jsonReq,
            parseBody: () => c.req.parseBody(),
            formData: () => c.req.formData(),
          },
        },
        "procedure",
        routePath,
      ),
    );
  };
}

export function buildApiRouter(params: {
  storageRouteHandler: unknown;
  storageUploadRouteHandler?: unknown;
  getQueryDef(name: string): QueryDef | undefined;
  mutationMap: MutationMap;
  queryProcedureMap?: ProcedureQueryMap;
  mutationProcedureMap?: ProcedureMutationMap;
  buildCtx(context: any): any;
  executeWithTimeout: ExecuteWithTimeout;
  parseArgs(schema: unknown, args: unknown): unknown;
  validationErrorCtor: ValidationErrorCtor;
  isBaas: boolean;
  logger?: LoggerLike;
}) {
  const mixedUserIdChecker = createMixedUserIdChecker({ logger: params.logger });
  const queryProcedureMap = params.queryProcedureMap ?? new Map();
  const mutationProcedureMap = params.mutationProcedureMap ?? new Map();
  const procedureRpcHandler = new ProcedureRpcHandler();
  const unifiedQueryHandler = createUnifiedQueryRouteHandler({
    getQueryDef: params.getQueryDef,
    queryProcedureMap,
    buildCtx: params.buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    parseArgs: params.parseArgs,
    validationErrorCtor: params.validationErrorCtor,
    isBaas: params.isBaas,
    procedureRpcHandler,
    checkMixedUserIds: mixedUserIdChecker,
    logger: params.logger,
  });
  const unifiedMutationHandler = createUnifiedMutationRouteHandler({
    mutationMap: params.mutationMap,
    mutationProcedureMap,
    buildCtx: params.buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    parseArgs: params.parseArgs,
    validationErrorCtor: params.validationErrorCtor,
    procedureRpcHandler,
    logger: params.logger,
  });
  return new Hono()
    .get("/", (c) => c.json(createApiRootPayload()))
    .post("/query", unifiedQueryHandler as never)
    .post("/mutation", unifiedMutationHandler as never)
    .get("/storage/:id", params.storageRouteHandler as never)
    .put(
      "/storage/upload/:uploadId",
      (params.storageUploadRouteHandler ??
        ((c: { json: (body: unknown, status?: number) => unknown }) =>
          c.json({ error: "Direct upload is not supported" }, 501))) as never,
    );
}
